import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'

import FormSelect from '../inputFields/FormSelect'
import FormInput from '../inputFields/FormInput'
import FormattedInput from '../inputFields/FormattedInput'
import { config } from '../../config'
import { provinces } from '../../util/consts'
import { theme } from '../../themes/theme'
import { formatPostalCode } from '../../util/strings'
import { FlexColumn, FormRow, HeaderWithButtons, MedBGContainer } from '../styles/Containers'
import { formSection } from '../styles/FormStyleConfig'
import { InfoMessage } from '../common/UserMessage'
import { SecondaryButton } from '../common/Button'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'
import DatabaseFile from '../files/DatabaseFile'

const Link = styled.a`
  color: ${theme.colors.text.link};
`

function PropertyAddressForm({ sectionTitle, id, showDelete = true, onDelete = () => {}, format = 'default', disabled = false }) {
  const { watch, getValues, setValue, formState } = useFormContext()
  const [defaultProvince, setDefaultProvince] = useState()

  useEffect(() => {
    setDefaultProvince(getValues(id)?.province || '')
  }, [])

  const existingFiles = watch(`${id}.existingFiles`)
  const files = watch(`${id}.files`)

  function handleUploadFiles(acceptedFiles) {
    const startingFiles = files || []
    setValue(`${id}.files`, startingFiles.concat(acceptedFiles))
  }

  function handleDelete(file) {
    const filteredFiles = files.filter(f => f !== file)
    setValue(`${id}.files`, filteredFiles)
  }

  function handleDeleteExistingFile(fileId) {
    const filteredFiles = existingFiles.filter(f => f.id !== fileId)
    setValue(`${id}.existingFiles`, filteredFiles)
  }

  const fileError = !!formState.errors?.[id]?.files
  const fileHelperText = formState.errors?.[id]?.files?.message

  const TitleComponent = formSection[format].font.formTitle
  const TextComponent = formSection[format].font.bodyText
  const basis = formSection[format].muiTextField.basis

  return (
    <FlexColumn gap={formSection[format].gap.div}>
      <HeaderWithButtons align="center">
        <TitleComponent>{sectionTitle}</TitleComponent>
        {showDelete && (
          <SecondaryButton onClick={onDelete} disabled={disabled}>
            Remove Address
          </SecondaryButton>
        )}
      </HeaderWithButtons>
      <FormRow gap={formSection[format].gap.row}>
        <FormInput id={`${id}.address`} label="Address" textFieldFormat={format} basis={basis} disabled={disabled} />
        <FormInput id={`${id}.unit`} label="Unit" textFieldFormat={format} basis={basis} disabled={disabled} />
        <FormInput id={`${id}.city`} label="City" textFieldFormat={format} basis={basis} disabled={disabled} />
        {defaultProvince !== undefined && (
          <FormSelect
            id={`${id}.province`}
            label="Province"
            options={provinces}
            defaultValue={defaultProvince}
            textFieldFormat={format}
            basis={basis}
            disabled={disabled}
          />
        )}
        <FormattedInput
          id={`${id}.postalCode`}
          label="Postal Code"
          handleFormatValue={formatPostalCode}
          textFieldFormat={format}
          basis={basis}
          disabled={disabled}
        />
        <FormInput id={`${id}.legalDescription`} label="Legal Description" textFieldFormat={format} basis={basis} disabled={disabled} />
      </FormRow>
      <InfoMessage>
        If there is more than one person on title for the subject property, please provide a signed{' '}
        {config.foi.request.addressAuthConfig || config.foi.request.generalAuthConfig ? (
          <Link href={config.foi.request.addressAuthConfig || config.foi.request.generalAuthConfig} target="_blank" rel="noopener noreferrer">
            Authorization to Release form
          </Link>
        ) : (
          <>Authorization to Release form</>
        )}
        .
      </InfoMessage>
      {((files && files.length > 0) || (existingFiles && existingFiles.length > 0)) && (
        <FlexColumn gap="10px">
          <TextComponent>The below file(s) will be uploaded as signed consent.</TextComponent>
          <MedBGContainer padding="5px 20px 5px 5px">
            {existingFiles &&
              existingFiles.length > 0 &&
              existingFiles.map(f => {
                return (
                  <DatabaseFile
                    key={f.id}
                    file={f}
                    onDelete={handleDeleteExistingFile}
                    disabled={disabled}
                    buttonAction="softDelete"
                    spacing="medium"
                  />
                )
              })}
            {files &&
              files.length > 0 &&
              files.map((f, i) => {
                return <BrowserFile key={i} file={f} onDelete={() => handleDelete(f)} loading={disabled} />
              })}
          </MedBGContainer>
        </FlexColumn>
      )}
      {!disabled && (
        <FlexColumn gap="10px">
          <TextComponent>Drag and drop or browse below to upload signed consent for each other person on title.</TextComponent>
          <UploadFile onUpload={handleUploadFiles} condensed={formSection[format].uploadCondensed} />
          {fileError && <ErrorText role="alert">{fileHelperText}</ErrorText>}
        </FlexColumn>
      )}
    </FlexColumn>
  )
}

PropertyAddressForm.propTypes = {
  sectionTitle: PropTypes.string,
  id: PropTypes.string,
  showDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  format: PropTypes.oneOf(['default', 'condensed']),
  disabled: PropTypes.bool
}

export default PropertyAddressForm
